import { useState, type FormEvent } from "react";
import { Button, TextArea } from "@/components/ui/FormControls";

/**
 * 템플릿,산정방식,예시,용어처럼 구조가 깊은 종류는 JSON 으로 고친다.
 * 문법 오류는 여기서 잡고, 스키마 검증은 부모(MasterPage)가 zod 로 한다.
 */
export function JsonEditor({ initial, keyField, lockKey, busy, onSubmit, onCancel }: {
  initial: Record<string, unknown>;
  keyField: string;
  lockKey: boolean;
  busy: boolean;
  onSubmit: (obj: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(initial, null, 2));
  const [syntax, setSyntax] = useState<string | null>(null);

  function parse(): Record<string, unknown> | null {
    try {
      const v: unknown = JSON.parse(text);
      if (!v || typeof v !== "object" || Array.isArray(v)) {
        setSyntax("객체({ … }) 하나여야 합니다.");
        return null;
      }
      setSyntax(null);
      return v as Record<string, unknown>;
    } catch (e) {
      setSyntax(`JSON 문법 오류: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const v = parse();
    if (!v) return;
    if (lockKey && String(v[keyField] ?? "") !== String(initial[keyField] ?? "")) {
      setSyntax(`${keyField} 는 바꿀 수 없습니다. 새 항목을 만들려면 '새 항목' 을 씁니다.`);
      return;
    }
    onSubmit(v);
  }

  function format() {
    const v = parse();
    if (v) setText(JSON.stringify(v, null, 2));
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-content">
      <div className="json-wrap">
        <TextArea label="JSON" spellCheck={false} value={text} onChange={(e) => setText(e.target.value)} hint={`키 필드 ${keyField}${lockKey ? " 는 고정" : " 는 새 값으로"}. 저장 전에 스키마로 검사합니다`} />
      </div>
      {syntax && (
        <p role="alert" className="note note-danger text-label">
          {syntax}
        </p>
      )}
      <div className="form-actions">
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "저장 중…" : "저장"}
        </Button>
        <Button variant="secondary" onClick={format} disabled={busy}>정리(들여쓰기)</Button>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>취소</Button>
      </div>
    </form>
  );
}
